import { captureException } from '@sentry/browser';
import { Canvg } from 'canvg';
import JsBarcode from 'jsbarcode';
import { BarcodeType } from './types/barcode.type';
import { nextId } from './utils/next-id.fn';
import { nextTick } from './utils/next-tick.fn';

export class Barcode {
  private svgMarkup: string;
  private _svgCanvas!: SVGElement;

  public dataUrl: string;

  constructor(
    public value: string,
    private additionalText: string,
    svgCanvas?: SVGElement,
  ) {
    if (!svgCanvas) {
      this._svgCanvas = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg',
      );
    } else {
      this._svgCanvas = svgCanvas;
    }
  }

  createSvgCanvas(parent?: HTMLElement) {
    return new Promise<void>(resolve =>
      requestAnimationFrame(() => {
        const barcodeId = nextId();
        this._svgCanvas.classList.add(`barcode-${barcodeId}`);
        this._svgCanvas.classList.add(`d-none`);

        if (parent) {
          parent.appendChild(this._svgCanvas);
        } else {
          document.body.appendChild(this._svgCanvas);
        }

        resolve();
      }),
    );
  }

  renderSvg(format: BarcodeType) {
    return new Promise<void>((resolve, reject) =>
      requestAnimationFrame(() => {
        try {
          JsBarcode(this._svgCanvas, this.value, {
            fontSize: 16,
            format,
          });
        } catch (e) {
          reject(typeof e === 'string' ? new Error(e) : e);
          return;
        }

        if (this.additionalText) {
          this._svgCanvas.classList.add(`invisible`);
          this._svgCanvas.classList.remove(`d-none`);
          this.addAdditionalText();
          this._svgCanvas.classList.remove(`invisible`);
          this._svgCanvas.classList.add(`d-none`);
        }

        this._svgCanvas.removeAttributeNS(null, 'height');

        const viewBox = this._svgCanvas.getAttributeNS(null, 'viewBox');
        const [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] =
          viewBox?.split(' ') ?? '';
        this._svgCanvas.setAttributeNS(
          null,
          'width',
          `${parseInt(viewBoxWidth, 10) * 2}px`,
        );

        this.svgMarkup = this._svgCanvas.outerHTML;
        resolve();
      }),
    );
  }

  addAdditionalText() {
    const backgroundRect = this._svgCanvas.querySelector('rect');

    if (backgroundRect === null) return;

    const barcodeGroup = this._svgCanvas.querySelector('g');

    if (barcodeGroup === null) return;

    const width = parseInt(
      backgroundRect?.getAttributeNS(null, 'width') ?? '0',
      10,
    );
    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.classList.add('additional-text');

    rect.setAttributeNS(null, 'width', width.toString());
    rect.setAttributeNS(null, 'height', '20');
    rect.setAttributeNS(null, 'x', '0');
    rect.setAttributeNS(null, 'y', '0');
    group.setAttributeNS(null, 'width', width.toString());
    group.setAttributeNS(null, 'height', '20');
    group.setAttributeNS(null, 'x', '0');
    group.setAttributeNS(null, 'y', '0');
    rect.style.fill = '#fff';

    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.style.font = '16px monospace';
    text.setAttributeNS(null, 'x', (width / 2).toString());
    text.setAttributeNS(null, 'y', '16');
    text.setAttributeNS(null, 'height', '20');
    text.setAttributeNS(null, 'width', width.toString());
    text.setAttributeNS(null, 'text-anchor', 'middle');
    text.setAttributeNS(null, 'fill', '#000');
    text.textContent = this.additionalText;

    group.appendChild(rect);
    group.appendChild(text);

    this._svgCanvas.appendChild(group);

    const viewBox = this._svgCanvas.getAttributeNS(null, 'viewBox');
    const textBBox = text.getBBox();
    const [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] =
      viewBox?.split(' ') ?? '';

    const newWidth = width < textBBox.width ? textBBox.width : width;

    this._svgCanvas.setAttributeNS(
      null,
      'viewBox',
      `${viewBoxX} ${viewBoxY} ${newWidth} ${viewBoxHeight}`,
    );

    this._svgCanvas.setAttributeNS(null, 'width', `${newWidth}px`);
    backgroundRect?.setAttributeNS(null, 'width', `${newWidth}px`);
    rect.setAttributeNS(null, 'width', `${newWidth}px`);
    group.setAttributeNS(null, 'width', `${newWidth}px`);
    text.setAttributeNS(null, 'width', `${newWidth}px`);

    text.setAttributeNS(null, 'x', (newWidth / 2).toString());

    const barcodeGroupBBox = barcodeGroup.getBBox();
    const newBarcodeGroupBoxTranslateX =
      (newWidth - barcodeGroupBBox.width) / 2;
    barcodeGroup.setAttributeNS(
      null,
      'transform',
      `translate(${newBarcodeGroupBoxTranslateX},10)`,
    );
  }

  createImageDataUrl(height: number, width: number, quality: number) {
    return new Promise<void>(resolve =>
      requestAnimationFrame(() => {
        var canvas = document.createElement('canvas');

        canvas.width = width * quality * 8;
        canvas.height = height * quality * 8;

        var ctx = canvas.getContext('2d');

        if (ctx !== null && ctx !== undefined) {
          ctx.fillStyle = '#fff'; /// set white fill style
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          var options = {
            ignoreMouse: true,
            ignoreAnimation: true,
            ignoreDimensions: true,
          };

          nextTick(() => {
            const v = Canvg.fromString(
              ctx as CanvasRenderingContext2D,
              this.svgMarkup,
              options,
            );
            v.start();

            nextTick(() => {
              this.dataUrl = canvas.toDataURL('image/png', 1);
              resolve();
            });
          });
        } else {
          resolve();
        }
      }),
    );
  }

  removeSvgCanvas() {
    return new Promise<void>(resolve =>
      requestAnimationFrame(() => {
        this._svgCanvas.remove();
        resolve();
      }),
    );
  }

  async render(
    format: BarcodeType,
    height: number,
    width: number,
    quality: number,
  ) {
    await this.createSvgCanvas();
    await this.renderSvg(format);
    await this.createImageDataUrl(height, width, quality);
    await this.removeSvgCanvas();
    await new Promise<void>(resolve => setTimeout(() => resolve(), 10));
  }
}
